/**
 * Schedule Management Page
 * Web component for managing location-level schedule items and rules
 */
import { useState, useCallback } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  HStack,
  VStack,
  Text,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Input,
  FormControl,
  FormLabel,
  useToast,
  Heading,
} from '@chakra-ui/react';
import { ChevronDownIcon, DeleteIcon, EditIcon, AddIcon } from '@chakra-ui/icons';
import { useLocationSchedule } from '../hooks/useLocationSchedule';
import {
  useCreateSchedulingRule,
  useUpdateSchedulingRule,
  useDeleteSchedulingRule,
  useRemoveItem,
  useAddPlaylist,
  useAddClip,
} from '../hooks/useLocationSchedule';
import type { ScheduleItem, SchedulingRule, CreateSchedulingRuleRequest } from '../types/schedule';
import { AddPlaylistModal } from '../../scenarios/components/AddPlaylistModal';
import { ClipPickerModal } from '../../alerts/components/ClipPickerModal';
import {
  isPlaylistItem,
  isFrequencyRule,
  isSpecificTimesRule,
  SchedulingMode,
} from '../types/schedule';

interface ScheduleManagementPageProps {
  locationId: string;
}

interface EditingState {
  item: ScheduleItem;
  rule: SchedulingRule | null;
}

export function ScheduleManagementPage({ locationId }: ScheduleManagementPageProps) {
  const toast = useToast();
  const [editingState, setEditingState] = useState<EditingState | null>(null);
  const [formError, setFormError] = useState<string>('');

  const { data: schedule, isLoading, error, refetch } = useLocationSchedule(locationId);
  const removeItem = useRemoveItem(locationId);
  const addPlaylist = useAddPlaylist(locationId);
  const addClip = useAddClip(locationId);
  const playlistPicker = useDisclosure();
  const clipPicker = useDisclosure();

  const createRule = useCreateSchedulingRule(locationId, editingState?.item.id || '');
  const updateRule = useUpdateSchedulingRule(
    locationId,
    editingState?.item.id || '',
    editingState?.rule?.id || ''
  );
  const deleteRule = useDeleteSchedulingRule(
    locationId,
    editingState?.item.id || '',
    editingState?.rule?.id || ''
  );

  const {
    isOpen: isRuleEditorOpen,
    onOpen: onRuleEditorOpen,
    onClose: onRuleEditorClose,
  } = useDisclosure();

  const handleEditRule = useCallback(
    (item: ScheduleItem, rule: SchedulingRule | null = null) => {
      setEditingState({ item, rule });
      setFormError('');
      onRuleEditorOpen();
    },
    [onRuleEditorOpen]
  );

  const handleDeleteItem = useCallback(
    async (itemId: string) => {
      try {
        await removeItem.mutateAsync(itemId);
        toast({
          title: 'Success',
          description: 'Item removed successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to remove item',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [removeItem, toast]
  );

  const handleSaveRule = useCallback(
    async (formData: CreateSchedulingRuleRequest) => {
      setFormError('');

      try {
        if (editingState?.rule) {
          await updateRule.mutateAsync(formData);
          toast({
            title: 'Success',
            description: 'Rule updated',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        } else {
          await createRule.mutateAsync(formData);
          toast({
            title: 'Success',
            description: 'Rule created',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }
        onRuleEditorClose();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save rule';
        setFormError(message);
        toast({
          title: 'Error',
          description: message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [editingState, updateRule, createRule, toast, onRuleEditorClose]
  );

  const handleDeleteRule = useCallback(async () => {
    try {
      await deleteRule.mutateAsync();
      toast({
        title: 'Success',
        description: 'Rule deleted',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onRuleEditorClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete rule';
      setFormError(message);
      toast({
        title: 'Error',
        description: message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [deleteRule, toast, onRuleEditorClose]);

  if (isLoading) {
    return (
      <VStack justify="center" align="center" h="400px">
        <Spinner size="lg" color="blue.500" />
        <Text>Loading schedule...</Text>
      </VStack>
    );
  }

  if (error) {
    return (
      <VStack gap={4} p={4}>
        <Text color="red.500" fontWeight="bold">
          Error loading schedule
        </Text>
        <Text fontSize="sm" color="gray.600">
          {error.message}
        </Text>
        <Button colorScheme="blue" onClick={() => refetch()}>
          Retry
        </Button>
      </VStack>
    );
  }

  const items = schedule?.items || [];

  return (
    <VStack align="stretch" gap={6}>
      <HStack justify="space-between">
        <Heading size="md">Schedule Items</Heading>
        <HStack gap={2}>
          <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={playlistPicker.onOpen}>
            Add Playlist
          </Button>
          <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={clipPicker.onOpen}>
            Add Clip
          </Button>
        </HStack>
      </HStack>

      {items.length === 0 ? (
        <Box
          p={8}
          textAlign="center"
          borderRadius="md"
          bg="gray.50"
          borderWidth={1}
          borderColor="gray.200"
        >
          <Text fontSize="lg" fontWeight="600" color="gray.700">
            No schedule items yet
          </Text>
          <Text fontSize="sm" color="gray.600" mt={2}>
            Add playlists or clips to schedule content for this location
          </Text>
        </Box>
      ) : (
        <Box overflowX="auto" borderWidth={1} borderColor="gray.200" borderRadius="md">
          <Table variant="simple" size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Item</Th>
                <Th>Type</Th>
                <Th>Rules</Th>
                <Th w="100px">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {items.map((item) => (
                <Tr key={item.id}>
                  <Td>
                    <VStack align="start" gap={1}>
                      <Text fontWeight="600">
                        {isPlaylistItem(item)
                          ? item.playlistName
                          : item.contentName}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Position: {item.position}
                      </Text>
                    </VStack>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {isPlaylistItem(item)
                        ? `Playlist (${item.playlistType})`
                        : 'Clip'}
                    </Text>
                  </Td>
                  <Td>
                    <VStack align="start" gap={1}>
                      {item.rules.length === 0 ? (
                        <Text fontSize="sm" color="gray.500" fontStyle="italic">
                          No rules
                        </Text>
                      ) : (
                        item.rules.map((rule) => (
                          <HStack
                            key={rule.id}
                            fontSize="xs"
                            bg="blue.50"
                            px={2}
                            py={1}
                            borderRadius="sm"
                            justifyContent="space-between"
                            gap={2}
                          >
                            <Text>{rule.badge}</Text>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="blue"
                              onClick={() => handleEditRule(item, rule)}
                            >
                              Edit
                            </Button>
                          </HStack>
                        ))
                      )}
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="blue"
                        onClick={() => handleEditRule(item, null)}
                      >
                        Add Rule
                      </Button>
                    </VStack>
                  </Td>
                  <Td>
                    <Menu>
                      <MenuButton as={Button} size="sm" variant="ghost">
                        <ChevronDownIcon />
                      </MenuButton>
                      <MenuList>
                        <MenuItem icon={<EditIcon />} onClick={() => handleEditRule(item)}>
                          Edit
                        </MenuItem>
                        <MenuItem
                          icon={<DeleteIcon />}
                          color="red.500"
                          onClick={() => handleDeleteItem(item.id)}
                        >
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Rule Editor Modal */}
      {editingState && (
        <RuleEditorModal
          isOpen={isRuleEditorOpen}
          onClose={onRuleEditorClose}
          item={editingState.item}
          rule={editingState.rule}
          onSave={handleSaveRule}
          onDelete={editingState.rule ? handleDeleteRule : undefined}
          error={formError}
          isSaving={createRule.isPending || updateRule.isPending}
          isDeleting={deleteRule.isPending}
        />
      )}

      <AddPlaylistModal
        isOpen={playlistPicker.isOpen}
        onClose={playlistPicker.onClose}
        existingPlaylistIds={new Set(items.filter(isPlaylistItem).map((i) => i.playlistId))}
        onAdd={async (added) => {
          for (const p of added) {
            try {
              await addPlaylist.mutateAsync(p.playlistId);
            } catch {
              /* surfaced via list refresh */
            }
          }
        }}
      />
      <ClipPickerModal
        isOpen={clipPicker.isOpen}
        onClose={clipPicker.onClose}
        title="Add Clip"
        onSelect={(clip) =>
          addClip.mutate(clip.id, {
            onError: () =>
              toast({ title: 'Error', description: 'Failed to add clip', status: 'error', duration: 3000 }),
          })
        }
      />
    </VStack>
  );
}

// Rule Editor Modal Component
interface RuleEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: ScheduleItem;
  rule: SchedulingRule | null;
  onSave: (data: CreateSchedulingRuleRequest) => Promise<void>;
  onDelete?: () => Promise<void>;
  error: string;
  isSaving: boolean;
  isDeleting: boolean;
}

function RuleEditorModal({
  isOpen,
  onClose,
  item,
  rule,
  onSave,
  onDelete,
  error,
  isSaving,
  isDeleting,
}: RuleEditorModalProps) {
  const [mode, setMode] = useState<SchedulingMode>(
    rule?.schedulingMode || SchedulingMode.FREQUENCY
  );
  const [frequency, setFrequency] = useState<string>(
    (rule && isFrequencyRule(rule) ? rule.frequency : 1).toString()
  );
  const [specificTimes, setSpecificTimes] = useState<string>(
    rule && isSpecificTimesRule(rule) ? rule.specificTimes.join(', ') : ''
  );
  const [activeFrom, setActiveFrom] = useState<string>(rule?.activeFrom || '');
  const [activeUntil, setActiveUntil] = useState<string>(rule?.activeUntil || '');
  const [hasActiveHours, setHasActiveHours] = useState<boolean>(
    !!(rule?.activeFrom && rule?.activeUntil)
  );

  const itemName =
    item.type === 'playlist' ? item.playlistName : item.contentName;

  const handleSubmit = async () => {
    const formData: CreateSchedulingRuleRequest = {
      schedulingMode: mode,
      activeFrom: hasActiveHours ? activeFrom : null,
      activeUntil: hasActiveHours ? activeUntil : null,
    };

    if (mode === SchedulingMode.FREQUENCY) {
      formData.frequency = parseInt(frequency, 10);
    } else {
      formData.specificTimes = specificTimes
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
    }

    await onSave(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {rule ? 'Edit Rule' : 'Add Rule'} — {itemName}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody gap={4}>
          <VStack gap={4}>
            {/* Mode Selection */}
            <FormControl>
              <FormLabel>Mode</FormLabel>
              <HStack gap={2}>
                <Button
                  flex={1}
                  colorScheme={mode === SchedulingMode.FREQUENCY ? 'blue' : 'gray'}
                  variant={mode === SchedulingMode.FREQUENCY ? 'solid' : 'outline'}
                  onClick={() => setMode(SchedulingMode.FREQUENCY)}
                >
                  Frequency
                </Button>
                <Button
                  flex={1}
                  colorScheme={mode === SchedulingMode.SPECIFIC_TIMES ? 'blue' : 'gray'}
                  variant={mode === SchedulingMode.SPECIFIC_TIMES ? 'solid' : 'outline'}
                  onClick={() => setMode(SchedulingMode.SPECIFIC_TIMES)}
                >
                  Specific Times
                </Button>
              </HStack>
            </FormControl>

            {/* Frequency Input */}
            {mode === SchedulingMode.FREQUENCY && (
              <FormControl>
                <FormLabel>Frequency (per hour)</FormLabel>
                <Input
                  type="number"
                  min="1"
                  max="12"
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  placeholder="1"
                />
              </FormControl>
            )}

            {/* Specific Times Input */}
            {mode === SchedulingMode.SPECIFIC_TIMES && (
              <FormControl>
                <FormLabel>Specific Times (HH:MM)</FormLabel>
                <Input
                  as="textarea"
                  placeholder="09:00, 12:00, 17:00"
                  value={specificTimes}
                  onChange={(e) => setSpecificTimes(e.target.value)}
                  rows={3}
                />
              </FormControl>
            )}

            {/* Active Hours */}
            <FormControl>
              <HStack justify="space-between">
                <FormLabel>Active Hours</FormLabel>
                <Button
                  size="sm"
                  colorScheme={hasActiveHours ? 'blue' : 'gray'}
                  variant={hasActiveHours ? 'solid' : 'outline'}
                  onClick={() => setHasActiveHours(!hasActiveHours)}
                >
                  {hasActiveHours ? 'On' : 'Off'}
                </Button>
              </HStack>

              {hasActiveHours && (
                <HStack gap={2}>
                  <FormControl>
                    <FormLabel fontSize="sm">From</FormLabel>
                    <Input
                      type="time"
                      value={activeFrom}
                      onChange={(e) => setActiveFrom(e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Until</FormLabel>
                    <Input
                      type="time"
                      value={activeUntil}
                      onChange={(e) => setActiveUntil(e.target.value)}
                    />
                  </FormControl>
                </HStack>
              )}
            </FormControl>

            {/* Error Message */}
            {error && (
              <Box bg="red.50" p={3} borderRadius="md" borderLeftWidth={4} borderLeftColor="red.500">
                <Text color="red.700" fontSize="sm">
                  {error}
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter gap={2}>
          {rule && onDelete && (
            <Button
              colorScheme="red"
              variant="ghost"
              isLoading={isDeleting}
              onClick={onDelete}
            >
              Delete
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            isLoading={isSaving}
            onClick={handleSubmit}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
